import { t } from "@lingui/macro";
import {
  ExpenseBudgetItemTypeEnum,
  type ExpenseItemInput,
  ExpenseTypeEnum,
  type PaymentType,
} from "@src/__generated__/graphql";
import { BudgetItemStore } from "@src/components/modules/expenses/Create/components/BudgetItemsCard/BudgetItemStore";
import { appStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { can } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { denominate } from "@src/utils/formatters";
import { gt, required } from "@src/utils/forms/validators";
import { NumberModel } from "@src/utils/models";
import { hoursToSeconds } from "date-fns";
import { FieldState, FormState, FormStateLazy } from "formstate";
import { uniqueId } from "lodash";
import { action, autorun, computed, makeObservable, observable } from "mobx";
import type { TAddExpenseItemArgs } from "../models";
import type { Store } from "../store";
import { budgetItemForm, type TBudgetItemForm } from "./ExpenseItemForm";
import { ExpenseVatItemForm } from "./ExpenseVatItemForm";
import { FileInputForm } from "./FileInputForm";

export function getItemIdFromBudgetItemId(
  budgetItemId: string,
): string | undefined {
  const type = getItemTypeFromBudgetItemId(budgetItemId);
  const suffix = `-${type}`;

  if (!budgetItemId.endsWith(suffix)) return budgetItemId || undefined;

  const id = budgetItemId.slice(0, -suffix.length);
  return id || undefined;
}

export function getItemTypeFromBudgetItemId(
  budgetItemId: string,
): ExpenseBudgetItemTypeEnum {
  const itemType = Object.values(ExpenseBudgetItemTypeEnum).find((type) =>
    budgetItemId.endsWith(`-${type}`),
  );

  return itemType ?? ExpenseBudgetItemTypeEnum.ExpenseBudgetItem;
}

export class Form {
  private readonly defaultWorkspaceCompanyId =
    appStore.workspaceStore.defaultWorkspaceCompany?.id;
  private expenseItemRequired = ($: TBudgetItemForm[]) =>
    $.length === 0 && this.store.creditAccountsCardStore.items.length === 0
      ? t`Budget item is required`
      : null;
  private validateVatTotals = () =>
    this.store.vatTotalsMismatch
      ? t`VAT amounts on budget items must match the document VAT total`
      : null;
  private requiredIfCfoOrHigher = ($: string | undefined) => {
    if (can("expense_accessAccountingAttributes")) return required($);
    return null;
  };

  private identificationFieldsRequired = ($: string | undefined) => {
    if (
      !appStore.workspaceStore.settings?.expense_identification_fields_required
    )
      return null;
    return required($);
  };

  type = new FieldState<ExpenseTypeEnum | undefined>(
    ExpenseTypeEnum.Invoice,
  ).validators(required);
  contractor_id = new FieldState("");
  contract_id = new FieldState("");
  related_expense_ids = new FieldState<string[]>([]);
  issue_date = new FieldState<Date | undefined>(undefined).validators(required);
  date_of_supply = new FieldState<Date | undefined>(undefined)
    .validators(required)
    .onDidChange(({ newValue }) =>
      this.store.fetchExchangeRate({
        date: newValue ? toApiDate(newValue) : undefined,
      }),
    );
  due_date = new FieldState<Date | undefined>(undefined).validators(required);
  currency_id = new FieldState("")
    .validators(required)
    .onDidChange(({ newValue }) =>
      this.store.fetchExchangeRate({
        currencyId: newValue ?? undefined,
      }),
    );
  expense_vat_items = observable.map<string, ExpenseVatItemForm>([]);

  expense_items = observable.map<string, BudgetItemStore>([]);

  total_amount_includes_vat = new FieldState(false).onDidChange(
    ({ newValue }) => {
      const oldTotal = newValue
        ? this.store.totalAmountOfVatItemsWithoutVat
        : this.store.totalAmountOfVatItems;
      const newTotal = newValue
        ? this.store.totalAmountOfVatItems
        : this.store.totalAmountOfVatItemsWithoutVat;

      this.store.scaleBudgetItemsToExpenseTotal(oldTotal, newTotal);
      this.store.convertAmount(undefined, newTotal);
      this.store.recalculateBudgetItemsVat();
    },
  );
  total_amount_with_vat_in_currency = new FieldState<NumberModel>(
    new NumberModel(0, 0),
  ).validators(required);
  variable_symbol = new FieldState("").validators(
    this.identificationFieldsRequired,
  );
  constant_symbol = new FieldState("");
  specific_symbol = new FieldState("");
  internal_number = new FieldState("");
  document_number = new FieldState("").validators(
    this.identificationFieldsRequired,
  );
  note = new FieldState("").validators(this.identificationFieldsRequired);
  internal_note = new FieldState("");
  already_paid = new FieldState<boolean>(false);
  payment_type_id = new FieldState<PaymentType["id"] | undefined>(
    undefined,
  ).validators(required);
  paid_at = new FieldState<Date | undefined>(undefined).validators(required);
  files = [new FileInputForm(), new FileInputForm()];
  two_percent_fund = new FieldState(false);
  fond_contractor_id = new FieldState("");
  workspace_company_id = new FieldState(
    this.defaultWorkspaceCompanyId ?? "",
  ).validators(this.requiredIfCfoOrHigher);
  workspace_bank_account_id = new FieldState<string | undefined>(
    this.defaultWorkspaceCompanyId
      ? appStore.workspaceStore.getDefaultCompanyBankAccount(
          this.defaultWorkspaceCompanyId,
        )?.id
      : undefined,
  );
  userCreatedDate = new FieldState<Date>(new Date());
  store: Store;

  private requiredIfDifferentCurrency = ($: number | undefined) => {
    if (
      this.currency_id.value !== appStore.workspaceStore.settings?.currency.id
    )
      return required($);
    return null;
  };
  exchange_rate = new FieldState<number | undefined>(undefined)
    .validators(this.requiredIfDifferentCurrency, gt(0))
    .onDidChange(({ newValue }) =>
      this.store.convertAmount({
        exchangeRate: denominate(newValue ?? 1, EXCHANGE_RATE_DENOMINATION),
      }),
    );
  expense_no = new FieldState<string | undefined>(undefined).validators(
    this.requiredIfCfoOrHigher,
  );
  expense_sequence_id = new FieldState<string | undefined>(
    undefined,
  ).validators(this.requiredIfCfoOrHigher);
  private vatTotalsValidation = new FieldState(undefined).validators(
    this.validateVatTotals,
  );

  @computed
  get expenseItemsForm() {
    return new FormState(
      Array.from(this.expense_items.values()).map((item) => item.form),
    ).validators(this.expenseItemRequired);
  }

  readonly _requiredFields = [
    this.type,
    this.currency_id,
    this.total_amount_with_vat_in_currency,
    this.exchange_rate,
    this.userCreatedDate,
    this.note,
    this.variable_symbol,
    this.document_number,
  ];

  /** Proxy form to validate fields as draft */
  _draftFormProxy = new FormStateLazy(() => {
    const fields: (FieldState<any> | FormState<any>)[] = [
      ...this._requiredFields,
    ];

    if (
      appStore.workspaceStore.settings?.requireAlreadyPaidSubfieldsForDrafts &&
      this.already_paid.value
    ) {
      fields.push(this.paid_at);
      fields.push(this.payment_type_id);
    }

    return fields;
  });

  /** Proxy form to validate fields as non-draft */
  _formProxy = new FormStateLazy(() => {
    const fields: (FieldState<any> | FormState<any>)[] = [
      ...this._requiredFields,
      this.vatTotalsValidation,
      ...[...this.expense_vat_items.values()].map((i) => i.$),
      this.expenseItemsForm,
      this.workspace_company_id,
      this.expense_no,
      this.expense_sequence_id,
    ];

    this.store.creditAccountsCardStore.items.forEach((item) => {
      fields.push(item);
    });

    // Bill.paid_at is required
    if (this.type.value === ExpenseTypeEnum.Bill && !this.already_paid.value) {
      this.already_paid.onChange(true);
    }

    if (this.already_paid.value) {
      fields.push(this.paid_at);
      fields.push(this.payment_type_id);
    }

    if (this.store.showDateInputForPohoda) {
      fields.push(this.issue_date);
      fields.push(this.date_of_supply);
      fields.push(this.due_date);
    }

    switch (this.type.value) {
      case ExpenseTypeEnum.Card:
      case ExpenseTypeEnum.Bill:
        fields.push(this.contractor_id);
        break;
      case ExpenseTypeEnum.Other:
      case ExpenseTypeEnum.Invoice:
      case ExpenseTypeEnum.Proforma:
      case ExpenseTypeEnum.PaymentReceipt:
      case ExpenseTypeEnum.Contract:
        fields.push(
          this.due_date,
          this.date_of_supply,
          this.contract_id,
          this.contractor_id.validators(required),
        );
        switch (this.type.value) {
          case ExpenseTypeEnum.Proforma:
          case ExpenseTypeEnum.Invoice:
            fields.push(this.issue_date);
            break;
          case ExpenseTypeEnum.Contract:
            fields.push(this.contract_id);
            break;
        }
        break;
    }

    return fields;
  });

  constructor(store: Store) {
    makeObservable(this);
    this.store = store;
    this.addExpenseVatItem = this.addExpenseVatItem.bind(this);
    this.removeExpenseVatItem = this.removeExpenseVatItem.bind(this);
    this.resetExpenseVatItems = this.resetExpenseVatItems.bind(this);
    this.serialize = this.serialize.bind(this);

    autorun(() => {
      const defaultBankAccountId =
        appStore.workspaceStore.getDefaultCompanyBankAccount(
          this.workspace_company_id.value,
        )?.id;

      if (!defaultBankAccountId) return;
      this.workspace_bank_account_id.onChange(defaultBankAccountId);
    });
  }

  addExpenseVatItem(args: {
    amount_in_currency: NumberModel;
    vat_percent_informative: number;
    vat_in_currency?: number;
  }): void {
    this.expense_vat_items.set(
      uniqueId(),
      new ExpenseVatItemForm(
        args.amount_in_currency,
        args.vat_percent_informative,
        args.vat_in_currency,
      ),
    );
  }

  removeExpenseVatItem(id: string): void {
    this.expense_vat_items.delete(id);
  }

  resetExpenseVatItems(): void {
    this.expense_vat_items.clear();
  }

  @action.bound
  addExpenseItem(
    amount_in_currency: NumberModel,
    args?: TAddExpenseItemArgs,
    setCostCentre = false,
  ): void {
    this.expense_items.set(
      uniqueId(),
      new BudgetItemStore(
        budgetItemForm({
          id: args?.id,
          brand_id: args?.brand_id,
          project_id: args?.project_id,
          accounting_account_id: args?.accounting_account_id,
          budget_item_id: args?.budget_item_id,
          budget_item_type: args?.budget_item_type,
          amount_in_currency,
          amount_in_percent: args?.amount_in_percent
            ? new NumberModel(args.amount_in_percent, 2)
            : new NumberModel(0, 2),
          approved: args?.approved ?? false,
          responsible_id: args?.responsible_id,
          approvals: args?.approvals ?? [],
          tracked_time: args?.tracked_time,
          time_tracking_work_type_id: args?.time_tracking_work_type_id,
          report_date: args?.report_date,
          task_id: args?.task_id,
          track_hours:
            !!args?.tracked_time ||
            !!args?.time_tracking_work_type_id ||
            !!args?.report_date,
          vatRate: args?.vatRate,
          vat_amount_in_currency:
            args?.vat_amount_in_currency != null
              ? new NumberModel(
                  args.vat_amount_in_currency,
                  amount_in_currency.denominator,
                )
              : undefined,
          amount_includes_vat: this.total_amount_includes_vat.value,
          accounting_centre_id: args?.accounting_centre_id,
          accounting_vat_classification_id:
            args?.accounting_vat_classification_id,
          tag_ids: args?.tag_ids,
          description_id: args?.description_id,
          custom_cost_ratio: args?.custom_cost_ratio,
        }),
        this.store,
        args?.planned_expense_usages ?? [],
        setCostCentre,
      ),
    );
  }

  resetExchangeRate(): void {
    this.exchange_rate.onChange(undefined);
  }

  async validateAsDraft() {
    const validation = await this._draftFormProxy.validate();
    await Promise.all(
      Array.from(this.expense_items.values()).map((itemStore) =>
        itemStore.syncPlannedExpenseMappings(),
      ),
    );
    return validation;
  }

  async validate() {
    if (this.expense_no.value) {
      const expenseNoExists = await this.store.checkInvoiceNumberExist(
        this.expense_no.value,
      );

      if (expenseNoExists) {
        return {
          hasError: true,
        };
      }
    }

    const validation = await this._formProxy.validate();
    await Promise.all(
      Array.from(this.expense_items.values()).map((itemStore) =>
        itemStore.syncPlannedExpenseMappings(),
      ),
    );
    return validation;
  }

  createId(id: string, type: ExpenseBudgetItemTypeEnum): string {
    return `${id}-${type}`;
  }

  getItemIdFromId(budgetItemId: string): string | undefined {
    return getItemIdFromBudgetItemId(budgetItemId);
  }

  getItemTypeFromId(budgetItemId: string): ExpenseBudgetItemTypeEnum {
    return getItemTypeFromBudgetItemId(budgetItemId);
  }

  @action.bound
  private serializeExpenseItems(): ExpenseItemInput[] {
    return Array.from(this.expense_items.values()).map((itemStore) => ({
      id: itemStore.form.$.id.value,
      brand_id: itemStore.form.$.brand_id.value,
      project_id: itemStore.form.$.project_id.$,
      amount_in_currency:
        itemStore.form.$.amount_in_currency.$.denominatedValue,
      amount_in_percent:
        itemStore.form.$.amount_in_percent.$.denominatedValue || null,
      accounting_account_id: itemStore.form.$.accounting_account_id.$,
      budget_item_id: this.getItemIdFromId(itemStore.form.$.budget_item_id.$),
      budget_item_type: itemStore.form.$.budget_item_type.value,
      approved: itemStore.form.$.approved.value,
      mandatory_id: itemStore.form.$.responsible_id.value,
      vat_rate_id: itemStore.form.$.vatRate.value?.id ?? undefined,
      vat_amount_in_currency:
        itemStore.form.$.vat_amount_in_currency.$.denominatedValue || null,
      accounting_centre_id: itemStore.form.$.accounting_centre_id.$,
      accounting_vat_classification_id:
        itemStore.form.$.accounting_vat_classification_id.$,
      tag_ids: itemStore.form.$.tag_ids.$ ?? [],
      expense_item_description_id: itemStore.form.$.description_id.$ ?? null,
      custom_cost_ratio: itemStore.form.$.custom_cost_ratio.$,
      planned_expense_usages: itemStore.plannedExpenseMappingStore.serialize(),
      positions:
        itemStore.form.$.budget_item_type.value ===
          ExpenseBudgetItemTypeEnum.OurWorkBudgetItem &&
        itemStore.form.$.track_hours.value
          ? [
              {
                time_tracking_work_type_id:
                  itemStore.form.$.time_tracking_work_type_id.$!,
                tracked_time: hoursToSeconds(itemStore.form.$.tracked_time.$),
                report_date: itemStore.form.$.report_date.value
                  ? toApiDate(itemStore.form.$.report_date.value)
                  : undefined,
                task_id: itemStore.form.$.task_id.$!,
              },
            ]
          : [],
    }));
  }

  serialize() {
    return {
      type: this.type.$!,
      contractor_id: this.contractor_id.$,
      contract_id: this.contract_id.$,
      issue_date: this.issue_date.$ && toApiDate(this.issue_date.$),
      date_of_supply: this.date_of_supply.$ && toApiDate(this.date_of_supply.$),
      due_date: this.due_date.$ && toApiDate(this.due_date.$),
      currency_id: this.currency_id.$,
      total_amount_includes_vat: this.total_amount_includes_vat.$,
      total_amount_with_vat_in_currency:
        this.total_amount_with_vat_in_currency.$.denominatedValue,
      variable_symbol: this.variable_symbol.$,
      constant_symbol: this.constant_symbol.$,
      specific_symbol: this.specific_symbol.$,
      internal_number: this.internal_number.$,
      document_number: this.document_number.$,
      note: this.note.$,
      internal_note: this.internal_note.$,
      payment_type_id:
        this.already_paid.value === false ? null : this.payment_type_id.$,
      paid_at:
        this.already_paid.value === false
          ? null
          : this.paid_at.$ && toApiDate(this.paid_at.$),
      expense_vat_items: Array.from(this.expense_vat_items.values()).map((i) =>
        i.serialize(),
      ),
      expense_items: this.serializeExpenseItems().concat(
        this.store.creditAccountsCardStore.serialize(),
      ),
      files: this.files
        .filter((i) => i.fileToUpload.value)
        .map((i) => i.serialize()),
      exchange_rate: this.exchange_rate.$
        ? denominate(
            this.exchange_rate.$,
            appStore.workspaceStore.settings?.exchange_rate_denomination ??
              EXCHANGE_RATE_DENOMINATION,
          )
        : 0,
      fond_contractor_id: this.fond_contractor_id.$ || undefined,
      workspace_company_id: this.workspace_company_id.$,
      workspace_bank_account_id: this.workspace_bank_account_id.$ ?? null,
      user_created_date: toApiDate(this.userCreatedDate.$),
      expense_no: this.expense_no.$,
      invoice_order_sequence_id: this.expense_sequence_id.$,
      related_expense_ids: this.related_expense_ids.$,
    };
  }
}
